import { HttpStatusError, MalformedResponseError, NetworkError } from "./errors";
import type { ListSolutionsParams, SolutionDto, SolutionListDto } from "./types";
import { validateSolution, validateSolutionList } from "./validation";

export type CoinApiMode = "proxy" | "rest";

export interface CoinApiOptions {
  /**
   * "proxy" (default): go through the same-origin `coin-api.php` allowlist
   * proxy described in WEB_DEPLOYMENT.md — `baseUrl` is the proxy script's
   * URL and every request becomes `?path=<api path>&...`. This is the real
   * production shape; the browser never talks to the FastAPI service
   * directly.
   *
   * "rest": call `baseUrl` as a normal REST origin (`{baseUrl}/solutions`).
   * Useful for local development directly against `uvicorn` without the
   * PHP proxy in front of it.
   */
  mode?: CoinApiMode;
  baseUrl?: string;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

const DEFAULT_PROXY_BASE_URL = "./coin-api.php";
const DEFAULT_REST_BASE_URL = "/";

export class CoinApi {
  private readonly mode: CoinApiMode;
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: CoinApiOptions = {}) {
    this.mode = options.mode ?? "proxy";
    this.baseUrl = options.baseUrl ?? (this.mode === "proxy" ? DEFAULT_PROXY_BASE_URL : DEFAULT_REST_BASE_URL);
    if (!options.fetchImpl && typeof fetch === "undefined") {
      throw new Error("no fetch implementation available; pass options.fetchImpl explicitly");
    }
    // `fetch` is a method of `Window`/`WorkerGlobalScope`, not a free
    // function — some browsers (Firefox in particular) throw "'fetch'
    // called on an object that does not implement interface Window" if it
    // is invoked detached from that receiver, which is exactly what
    // happens if the bare `fetch` reference is stored and called later via
    // `this.fetchImpl(...)`. Wrapping it as an explicit
    // `globalThis.fetch(...)` property-access call (rather than relying on
    // `.bind()`) is the form every engine accepts, since the receiver is
    // set by the member-access syntax itself, not by a runtime rebind.
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  async listSolutions(params: ListSolutionsParams = {}): Promise<SolutionListDto> {
    const url = this.buildUrl("/solutions", {
      is_closed: params.isClosed,
      sort: params.sort,
      order: params.order,
      limit: params.limit,
      offset: params.offset,
    });
    return validateSolutionList(await this.fetchJson(url));
  }

  /** Returns `null` on a 404 (solution not found) rather than throwing. */
  async getSolution(solutionHash: string): Promise<SolutionDto | null> {
    const url = this.buildUrl(`/solutions/${solutionHash}`);
    try {
      return validateSolution(await this.fetchJson(url));
    } catch (error) {
      if (error instanceof HttpStatusError && error.status === 404) return null;
      throw error;
    }
  }

  /**
   * A solution that can begin play exactly at `fromSquare` (0..63), for
   * seamless tile-to-tile continuation between tours. Returns `null` on a
   * 404 (nothing in the database can continue from that square yet) rather
   * than throwing -- callers are expected to fall back to their normal
   * selection strategy, the same as `getSolution`'s not-found case.
   */
  async getContinuation(fromSquare: number, options: { openOnly?: boolean } = {}): Promise<SolutionDto | null> {
    const url = this.buildUrl("/solutions/continuation", {
      from_square: fromSquare,
      open_only: options.openOnly,
    });
    try {
      return validateSolution(await this.fetchJson(url));
    } catch (error) {
      if (error instanceof HttpStatusError && error.status === 404) return null;
      throw error;
    }
  }

  private buildUrl(apiPath: string, query: Record<string, string | number | boolean | undefined> = {}): string {
    const origin = typeof window !== "undefined" ? window.location.href : "http://localhost/";
    if (this.mode === "proxy") {
      const url = new URL(this.baseUrl, origin);
      url.searchParams.set("path", apiPath.replace(/^\//, ""));
      this.appendQuery(url, query);
      return url.toString();
    }
    const normalizedBase = this.baseUrl.endsWith("/") ? this.baseUrl : `${this.baseUrl}/`;
    const url = new URL(normalizedBase + apiPath.replace(/^\//, ""), origin);
    this.appendQuery(url, query);
    return url.toString();
  }

  private appendQuery(url: URL, query: Record<string, string | number | boolean | undefined>): void {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  private async fetchJson(url: string): Promise<unknown> {
    let response: Response;
    try {
      response = await this.fetchImpl(url, { headers: { Accept: "application/json" } });
    } catch (error) {
      throw new NetworkError(
        `request to ${url} failed: ${error instanceof Error ? error.message : "unknown network error"}`
      );
    }
    if (!response.ok) {
      throw new HttpStatusError(response.status, `request to ${url} failed with status ${response.status}`);
    }
    try {
      return await response.json();
    } catch {
      throw new MalformedResponseError(`response from ${url} was not valid JSON`);
    }
  }
}
